import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { CircularProgress, Tooltip } from '@mui/material';
import { useHotkeys } from 'react-hotkeys-hook';
import { useHistory } from 'react-router-dom';
import cx from 'classnames';

import { updateDisableOptionInMenu, updateSecondaySelectedClips } from '../../../store/sharedStore/sharedStoreSlice';
import { SIDEBAR_MENU } from '../../../hoc/Layout/Sidebar/SIDEBAR_MENU';
import { ContainedButton, OutlinedButton } from '../../modules/Buttons';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { NOTIFICATION, PATHS, VIDEOFEED } from '../../../enums';
import SharedComposerListModal from './SharedComposerListModal';
import OnlineComposerModal from './OnlineComposerModal';
import { findSearchValue, getComposerWebPreferences } from '../../../helpers';
import { getTopicsToDisplay } from '../../../helpers/getTopicsToDisplay';
import styles from './SendClipModal.module.scss';
import { patchAddTopics } from '../../../api';
import Modal from '../../modules/Modal';
import { TRow } from '../../VideoPage';
import { useClipFavourites } from '../../ClipFavourites/context';

type TSendClipModal = {
  open: boolean;
  switchModal: (open?: boolean) => void;
  onCreateComposer?: () => void;
  skipConfirmation?: boolean;
  setSkipConfirmationModals?: (v: boolean) => void;
  selectedRows?: TRow[];
  videoRows?: TRow[];
  beforeSendToComposer?: (targetFeedId: string) => Promise<boolean>;
  showCurrentClipChoiceWhenMultipleSelected?: boolean;
  canCreateComposer?: boolean;
  sendToComposer?: (targetFeedId: string) => Promise<{ data: VIDEOFEED }>;
};

export type TAddTopicsBody = {
  destinationId: string;
  sourceId: string;
  topicsIds?: string[];
  clips?: { id: string; time_start: number; time_end: number }[];
  addOnBottom?: boolean;
};

enum ModalStep {
  Confirm = 1,
  SelectComposer = 2,
  InProgress = 3,
}

const SendClipModal: React.FC<TSendClipModal> = ({
  open,
  switchModal,
  onCreateComposer,
  skipConfirmation,
  setSkipConfirmationModals,
  selectedRows,
  videoRows,
  beforeSendToComposer,
  showCurrentClipChoiceWhenMultipleSelected = false,
  canCreateComposer = true,
  sendToComposer,
}) => {
  const dispatch = useAppDispatch();
  const notification = useSnackbar();
  const { reload: reloadFavourites } = useClipFavourites();
  const [step, setStep] = useState<ModalStep>(ModalStep.Confirm);
  const [indexOfSection, setIndexOfSection] = useState(PATHS.MY_REFLEX_COMPOSERS.path);
  const [selectedFeed, setSelectedFeed] = useState<VIDEOFEED | null>(null);
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const { sortedSelectedClips, markerPosition } = useAppSelector((state) => state.videoStore);
  const { composerRows, currentSelectedComposerClips, composerPlayControl } = useAppSelector((state) => state.composersStore);
  const { role, currentUser } = useAppSelector((state) => state.user);
  const { currentVideoThatInFrame, secondarySelectedClips, preserveSecondaryClipOrder } = useAppSelector((state) => state.sharedStore);
  const toComposerSelectionButtonRef = useRef<HTMLDivElement>(null);
  const proceedToComposerButtonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (skipConfirmation && open) {
      setStep(ModalStep.SelectComposer);
    }
  }, [skipConfirmation, open]);

  const topicsToDisplay = useMemo(
    () =>
      getTopicsToDisplay({
        sortedSelectedClips,
        currentSelectedComposerClips,
        secondarySelectedClips,
        composerRows,
      }),
    [sortedSelectedClips, currentSelectedComposerClips, secondarySelectedClips, composerRows],
  );

  const feedId = findSearchValue('feedId') as string;
  const selectedRowIds = selectedRows?.map((r) => String(r.id)) ?? [];
  const selectedRowClipCount = selectedRows?.reduce((sum, r) => sum + (r.clips?.length ?? 0), 0) ?? 0;
  const selectedClipsInRows = sortedSelectedClips.filter((c) => selectedRowIds.includes(String(c.topicId))).length;
  const isFullRowSelection =
    selectedRows && selectedRows.length > 0 && selectedRowClipCount > 0 && selectedClipsInRows === selectedRowClipCount;
  const showRowSelectionConfirm = secondarySelectedClips.length === 0 && isFullRowSelection && selectedRowClipCount > 1;
  const selectedClipCountForChoice = currentSelectedComposerClips.length > 0 ? currentSelectedComposerClips.length : sortedSelectedClips.length;
  const showMultiSelectionConfirm =
    secondarySelectedClips.length === 0 &&
    (showRowSelectionConfirm || (showCurrentClipChoiceWhenMultipleSelected && selectedClipCountForChoice > 1));

  const onClose = () => {
    switchModal(false);
    setLoading(false);
    setSelectedFeed(null);
    if (!sendToComposer) dispatch(updateSecondaySelectedClips([]));
    setTimeout(() => {
      setStep(1);
    }, 200);
    if (setSkipConfirmationModals) {
      setSkipConfirmationModals(false);
    }
  };

  const toContentPage = () => {
    setStep(ModalStep.SelectComposer);
  };

  const toUploadingPage = () => {
    setStep(ModalStep.InProgress);
  };

  const upload = async (selectedVideoFeedId: string) => {
    if (!sendToComposer && !currentVideoThatInFrame) return;
    try {
      setLoading(true);
      if (sendToComposer) {
        const res = await sendToComposer(selectedVideoFeedId);
        setSelectedFeed(res.data);
        return;
      }
      let selectedClips: { id: string; time_start: number; time_end: number }[] = sortedSelectedClips;
      if (currentSelectedComposerClips.length) {
        selectedClips = currentSelectedComposerClips;
      }
      if (secondarySelectedClips.length) {
        selectedClips = secondarySelectedClips;
      }
      const orderedClips = preserveSecondaryClipOrder ? selectedClips : selectedClips.slice().sort((a, b) => a.time_start - b.time_start);
      const clips = orderedClips.map((clip) => ({ id: clip.id, time_start: clip.time_start, time_end: clip.time_end }));
      const body: TAddTopicsBody = {
        clips,
        topicsIds: preserveSecondaryClipOrder
          ? Array.from(new Set(secondarySelectedClips.slice().sort((a, b) => a.rowIndex! - b.rowIndex!).map((clip) => clip.topicId!)))
          : Object.keys(topicsToDisplay),
        sourceId: feedId,
        destinationId: selectedVideoFeedId,
        addOnBottom: getComposerWebPreferences().sendClipsAddOnBottom,
      };
      const res = await patchAddTopics(body);
      reloadFavourites();
      setSelectedFeed(res.data);
    } catch (err) {
      if (sendToComposer) setStep(ModalStep.SelectComposer);
      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notification.showMessage(err?.response?.data?.message);
      } else {
        notification.showMessage(NOTIFICATION.NOT_UPDATED);
      }
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const onSelect = async (selectedVideoFeedId: string) => {
    if (beforeSendToComposer) {
      const canProceed = await beforeSendToComposer(selectedVideoFeedId);
      if (!canProceed) return;
    }
    toUploadingPage();
    upload(selectedVideoFeedId);
  };

  const sendAllSelected = () => {
    dispatch(updateSecondaySelectedClips([]));
    toContentPage();
  };

  const sendCurrentClip = () => {
    let currentClip: any = sortedSelectedClips.find(
      (c) => c.rowIndex === markerPosition.rowIndex && c.clipIndex === markerPosition.clipIndex,
    );
    if (!currentClip && videoRows?.[markerPosition.rowIndex]?.clips?.[markerPosition.clipIndex]) {
      const clip = videoRows[markerPosition.rowIndex].clips[markerPosition.clipIndex];
      currentClip = {
        ...clip,
        rowIndex: markerPosition.rowIndex,
        clipIndex: markerPosition.clipIndex,
        topicId: videoRows[markerPosition.rowIndex].id,
        topicName: videoRows[markerPosition.rowIndex].name,
      } as any;
    }
    if (!currentClip) {
      const rowIndex = composerPlayControl.currentRowIndex;
      const clipIndex = composerPlayControl.currentClipIndex;
      currentClip = currentSelectedComposerClips.find((c) => c.rowIndex === rowIndex && c.clipIndex === clipIndex) as any;
      if (!currentClip && composerRows?.[rowIndex]?.clips?.[clipIndex]) {
        const clip = composerRows[rowIndex].clips[clipIndex];
        currentClip = {
          ...clip,
          rowIndex,
          clipIndex,
          topicId: composerRows[rowIndex].id,
          topicName: composerRows[rowIndex].name,
        } as any;
      }
    }
    if (!currentClip) return;
    const resolvedRowIndex =
      typeof currentClip.rowIndex === 'number' ? currentClip.rowIndex : Number(composerPlayControl.currentRowIndex || 0);
    const resolvedRow = composerRows?.[resolvedRowIndex];
    const normalizedCurrentClip = {
      ...currentClip,
      rowIndex: resolvedRowIndex,
      clipIndex:
        typeof currentClip.clipIndex === 'number' ? currentClip.clipIndex : Number(composerPlayControl.currentClipIndex || 0),
      topicId: currentClip.topicId || resolvedRow?.id,
      topicName: currentClip.topicName || resolvedRow?.name,
    } as any;
    if (!normalizedCurrentClip.topicId) return;
    dispatch(updateSecondaySelectedClips([normalizedCurrentClip]));
    toContentPage();
  };

  const redirect = () => {
    if (
      selectedFeed &&
      selectedFeed.created_by &&
      typeof selectedFeed.created_by.id === 'string' &&
      selectedFeed.created_by.id === currentUser?.id
    ) {
      history.push(
        `${PATHS.COMPOSER_PAGE.path}?feedId=${selectedFeed.id}&fileside=true&feed=${0}&clipId=${0}&clip=0&pageType=${selectedFeed.type}`,
      );
    } else if (selectedFeed) {
      history.push(
        `${PATHS.COMPOSER_PAGE.path}?feedId=${selectedFeed.id}&fileside=true&feed=${0}&clipId=${0}&clip=0&pageType=${
          selectedFeed.type
        }&sharedComposer=true`,
      );
    }
    onClose();
  };

  const onSectionSelect = (path: string) => {
    setIndexOfSection(path);
  };

  const handleCreateComposer = () => {
    if (!canCreateComposer || !onCreateComposer) return;
    switchModal(false);
    onCreateComposer();
    if (setSkipConfirmationModals) {
      setSkipConfirmationModals(true);
    }
  };

  useEffect(() => {
    if (sendToComposer) return;
    dispatch(updateDisableOptionInMenu([PATHS.MY_FOLDERS.path]));

    return () => {
      dispatch(updateDisableOptionInMenu([]));
      dispatch(updateSecondaySelectedClips([]));
    };
  }, []);

  useHotkeys('Enter', () => {
    if (!open || loading) return;

    switch (step) {
      case ModalStep.Confirm:
        toComposerSelectionButtonRef?.current?.click();
        break;
      case ModalStep.InProgress:
        proceedToComposerButtonRef?.current?.click();
        break;
      default:
        break;
    }
  });

  return (
    <Modal open={open} onClose={!loading ? onClose : console.log} className={styles.wrapper} loading={loading}>
      {step === ModalStep.Confirm && (
        <div className={styles.confirmModalBody}>
          {showMultiSelectionConfirm ? (
            <>
              <div className={styles.modalTitle}>Do you want to send clip(s) to composer?</div>
              <ContainedButton onClick={sendAllSelected} className={styles.confirmBtn}>
                <div ref={toComposerSelectionButtonRef}>Send all {selectedClipCountForChoice} clip(s) to composer </div>
              </ContainedButton>
              <ContainedButton onClick={sendCurrentClip} className={styles.confirmBtn}>
                Send current playing clip to a composer
              </ContainedButton>
              <OutlinedButton onClick={onClose}>No, cancel</OutlinedButton>
            </>
          ) : (
            <>
              <div className={styles.modalTitle}>Would you like to send selected clip(s) to an online composer?</div>
              <div className={styles.fileList}>
                {Object.keys(topicsToDisplay).map((key) => (
                  <Tooltip key={key} title={topicsToDisplay[key].topicName}>
                    <div className={styles.topicRow}>
                      <div className={styles.topicName}>{topicsToDisplay[key].topicName}</div>
                      <div className={styles.clipCount}>
                        {topicsToDisplay[key].clipCount} {topicsToDisplay[key].clipCount === 1 ? 'clip' : 'clips'}
                      </div>
                    </div>
                  </Tooltip>
                ))}
              </div>
              <ContainedButton onClick={toContentPage} className={styles.confirmBtn}>
                <div ref={toComposerSelectionButtonRef}>Yes</div>
              </ContainedButton>
              <OutlinedButton onClick={onClose}>No, cancel</OutlinedButton>
            </>
          )}
        </div>
      )}

      {step === ModalStep.SelectComposer && (
        <div className={styles.feedListModal}>
          <OutlinedButton className={styles.closeBtn} onClick={onClose}>
            Close
          </OutlinedButton>
          <div className={styles.feedSidebar}>
            <div
              onClick={() => onSectionSelect(PATHS.MY_REFLEX_COMPOSERS.path)}
              key={PATHS.MY_REFLEX_COMPOSERS.label}
              className={cx(indexOfSection === PATHS.MY_REFLEX_COMPOSERS.path && styles.activeItem, styles.item)}
            >
              {PATHS.MY_REFLEX_COMPOSERS.label}
            </div>
            <div
              onClick={() => onSectionSelect(PATHS.SHARED_WITH_ME.path)}
              key={PATHS.SHARED_WITH_ME.label}
              className={cx(indexOfSection === PATHS.SHARED_WITH_ME.path && styles.activeItem, styles.item)}
            >
              {PATHS.SHARED_WITH_ME.label}
            </div>
          </div>
          <div className={styles.feedContent}>
            {indexOfSection === PATHS.MY_REFLEX_COMPOSERS.path && (
              <>
                <div className={styles.sectionLine}>Select Composer</div>
                <OnlineComposerModal
                  onSelect={onSelect}
                  disableMyFoldersInSidebar={true}
                  onCreateComposer={handleCreateComposer}
                  canCreateComposer={canCreateComposer}
                />
              </>
            )}
            {indexOfSection === PATHS.SHARED_WITH_ME.path && (
              <>
                <div className={styles.sectionLine}>Select Shared Composer</div>
                <SharedComposerListModal
                  onSelect={onSelect}
                  disableMyFoldersInSidebar={true}
                  onCreateComposer={handleCreateComposer}
                  canCreateComposer={canCreateComposer}
                />
              </>
            )}
          </div>
        </div>
      )}

      {step === ModalStep.InProgress && (
        <div className={styles.progressModal}>
          <div className={styles.progressTitle}>{loading ? 'Sending in progress...' : 'Sending completed'}</div>
          {loading && (
            <div className={styles.progressFileList}>
              {Object.keys(topicsToDisplay).map((key) => (
                <Tooltip key={key} title={topicsToDisplay[key].topicName}>
                  <div className={styles.topicRow}>
                    <div className={styles.topicName}>{topicsToDisplay[key].topicName}</div>
                    <div className={styles.clipCount}>
                      {topicsToDisplay[key].clipCount} {topicsToDisplay[key].clipCount === 1 ? 'clip' : 'clips'}
                    </div>
                  </div>
                </Tooltip>
              ))}

              <div className={styles.progressLoader}>{loading && <CircularProgress color="inherit" />}</div>
              {loading && (
                <div className={styles.progressHelperText}> We are processing your video content. Please wait for it to be finished. </div>
              )}
              {!loading && <div className={styles.progressHelperText}> Done </div>}
            </div>
          )}
          <ContainedButton onClick={redirect} disabled={loading && !selectedFeed} className={styles.viewVideoBtn}>
            <div ref={proceedToComposerButtonRef}>Proceed to composer</div>
          </ContainedButton>
          <OutlinedButton onClick={onClose} disabled={loading && !selectedFeed} className={styles.stayhereVideoBtn}>
            Stay here
          </OutlinedButton>
        </div>
      )}
    </Modal>
  );
};

export default SendClipModal;
